import { APIURL, appendFlatpickrBaseStyles, initFlatpickrInShadow, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getSymbolOptions, getTraderSMCardHTML, getWidgetDataURI, apiGet, toggleWidgetTheme, normalizeWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML, isEmptyWidgetData } from "./../../helpers";
import {
  WidgetConfig,
  WidgetEvent,
  WidgetEventHandler,
  WidgetEventType,
} from "./../../types";
import flatpickr from "flatpickr";
import { format } from "date-fns";

export class TraderverseCryptoMarket {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private selectedRange: string = "Last 3 Months";
  private startDate: string | null = null;
  private endDate: string | null = null;
  private calendarInstance: any = null;
  private selectedAction: string = "all";

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    const cryptoList = getSymbolOptions("crypto");
    const sym = String(this.config.symbol || "")
      .trim()
      .toUpperCase();
    const match =
      cryptoList.find((c) => c.stockId === sym) ||
      cryptoList.find((c) => c.symbol === sym) ||
      (sym.includes("_")
        ? cryptoList.find((c) => sym.startsWith(`${c.symbol}_`))
        : undefined);
    if (match) {
      this.config.symbol = match.symbol;
      this.config.stock = { ...match };
    } else {
      const first = cryptoList[0];
      this.config.symbol = first.symbol;
      this.config.stock = { ...first };
    }
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: "closed" });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const first = getSymbolOptions("crypto")[0];
    return {
      headerPinned: true,
      symbol: first.symbol,
      theme: "default-dark",
      width: "100%",
      height: "400px",
      autosize: true,
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === "string") {
      const el =
        document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) {
      const first = getSymbolOptions("crypto")[0];
      this.config.symbol = first.symbol;
      this.config.stock = { ...first };
    }
    this.createWidget();
    const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
    if (widget) widget.classList.add("loading");
    this.setDateRange(this.selectedRange);
    this.attachStyles();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove("loading");
    this.setupCalendar();
    this.emit("ready", {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private setDateRange(range: string): void {
    const today = new Date();
    let start = new Date();

    switch (range) {
      case "Current Week":
        start.setDate(today.getDate() - today.getDay());
        break;
      case "Last Week":
        start.setDate(today.getDate() - today.getDay() - 7);
        today.setDate(start.getDate() + 6);
        break;
      case "Last 2 Weeks":
        start.setDate(today.getDate() - 14);
        break;
      case "Last 3 Months":
        start.setMonth(today.getMonth() - 3);
        break;
      case "This Month":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      default:
        start.setMonth(today.getMonth() - 1);
        break;
    }

    this.startDate = start.toISOString().split("T")[0];
    this.endDate = today.toISOString().split("T")[0];
  }

  private createWidget(): void {
    let themeClass = "defaultDarkMode";
    let dataTheme = "default-dark";

    if (this.config.theme === "white") {
      themeClass = "whiteMode";
      dataTheme = "white";
    } else if (this.config.theme === "black") {
      themeClass = "blackMode";
      dataTheme = "black";
    } else if (this.config.theme === "default-lite") {
  themeClass = "defaultLightMode";
  dataTheme = "default-lite";
}

    const widgetHtml = `
         <div style="width: ${
           typeof this.config.width == "number"
             ? this.config.width + "px"
             : this.config.width
         };height: ${
      typeof this.config.height == "number"
        ? this.config.height + "px"
        : this.config.height
    };" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title crypto">${this.getTitleIconSvg()} Crypto Market</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div>
            </div>
          </div>
          <div class="inBody">
            <div class="optionsRow">
              ${
                this.config.hideSymbol
                  ? '<div class="item dropStyle01"></div>'
                  : `<div class="item dropStyle01">
                <div class="trader-sm-card" id="selected-symbol">
                  ${getTraderSMCardHTML(
                    this.config.stock ?? {
                      symbol: this.config.symbol!,
                      type: "crypto",
                      name: "",
                    }
                  )}
                </div>
                <span id="symbol-dropdown-placeholder"></span>
              </div>`
              }
              <span></span>


              

                <div class="filtersBox sideBar hidden">
                  <div class="item">
                    <select class="action-select">
                      <option value="all" selected>All</option>
                      <option value="hold">Hold</option>
                      <option value="upgrade">Upgrade</option>
                      <option value="downgrade">Downgrade</option>
                      <option value="initialise">Initialise</option>
                    </select>
                  </div>

                  <div class="item">
                    <select class="range-select">
                    <option value="Current Week" >Current Week</option>
                    <option value="Last Week">Last Week</option>
                    <option value="Last 2 Weeks">Last 2 Weeks</option>
                    <option value="Last 3 Months" selected>Last 3 Months</option>
                    <option value="This Month">This Month</option>
                  </select>
                  </div>
                  <div class="item">
                    <input id="calendarPicker" type="text" placeholder="Select Date Range" readonly />
                  </div>
                </div>


            </div>
            <div class="chartDv crypto-exchanges-scroll" id="cryptoExchangesContainer"></div>
          </div>
        </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
    const rangeSelect = this.shadowRoot.querySelector(
      ".range-select"
    ) as HTMLSelectElement;
    rangeSelect.addEventListener("change", () => {
      this.selectedRange = rangeSelect.value;
      this.setDateRange(this.selectedRange);
      this.setupChart(this.config.symbol!);
    });

    const actionSelect = this.shadowRoot.querySelector(
      ".action-select"
    ) as HTMLSelectElement;
    actionSelect.addEventListener("change", () => {
      this.selectedAction = actionSelect.value;
      this.updateDisplay();
    });

    const filtersToggleBtn = this.shadowRoot.querySelector(
      ".filters-toggle"
    ) as HTMLButtonElement | null;
    const filtersBox = this.shadowRoot.querySelector(
      ".filtersBox"
    ) as HTMLElement | null;
    if (filtersToggleBtn && filtersBox) {
      filtersToggleBtn.addEventListener("click", () => {
        filtersBox.classList.toggle("hidden");
      });
    }

    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown(
        (data: any) => this.onSelectChange(data),
        "crypto"
      );
      const placeholder = this.shadowRoot.getElementById(
        "symbol-dropdown-placeholder"
      );
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
  }

  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }

  /** `/api/v1/crypto/exchanges` expects a plain ticker (e.g. `BTC`). */
  private toCryptoApiSymbol(symbolOrStockId: string): string {
    const s = String(symbolOrStockId || "")
      .trim()
      .toUpperCase();
    if (/_USD_CRYPTO$/i.test(s)) return s.replace(/_USD_CRYPTO$/i, "");
    if (/_CRYPTO$/i.test(s)) return s.replace(/_CRYPTO$/i, "");
    if (!s.includes("_")) return s;
    return s.split("_")[0] || s;
  }

  private esc(s: unknown): string {
    return String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  private setupCalendar(): void {
    const calendarInput = this.shadowRoot.querySelector(
      "#calendarPicker"
    ) as HTMLInputElement;
    if (!calendarInput) return;

    let firstDateSelected = false;
    this.calendarInstance = initFlatpickrInShadow(calendarInput, this.shadowRoot, {
      dateFormat: "Y-m-d",
      closeOnSelect: false,
      onChange: (selectedDates: Date[]) => {
        if (!firstDateSelected) {
          this.startDate = format(selectedDates[0], "yyyy-MM-dd");
          firstDateSelected = true;
          calendarInput.value = `Start: ${this.startDate}`;
        } else {
          this.endDate = format(selectedDates[0], "yyyy-MM-dd");
          firstDateSelected = false;
          calendarInput.value = `${this.startDate} to ${this.endDate}`;
          this.calendarInstance.close();
          this.fetchCustomDateRange();
        }
      },
    });
  }

  private async fetchCustomDateRange(): Promise<void> {
    if (!this.startDate || !this.endDate) return;
    await this.setupChart(this.config.symbol!);
  }

  private async attachStyles(): Promise<void> {
    try {
      appendFlatpickrBaseStyles(this.shadowRoot);
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error("Failed to load CSS file:", err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const intervalDropdown = this.shadowRoot.querySelector(
      ".interval-dropdown"
    ) as HTMLSelectElement;
    intervalDropdown?.addEventListener("change", (e) => {
      const value = (e.target as HTMLSelectElement).value;
      this.setupChart(this.config.symbol!);
    });
  }

  private async setupChart(symbol: string): Promise<void> {
    const apiSymbol = this.toCryptoApiSymbol(symbol);
    const response = await apiGet({
      method: "GET",
      url: `${APIURL}/v1/crypto/exchanges`,
      query: { symbol: apiSymbol },
    });

    const data = await response.json();
    const raw = Array.isArray(data?.data) ? data.data : [];
    this.currentData = { data: raw, symbol: apiSymbol };
    this.updateDisplay();
    this.emit("dataUpdate", this.currentData);
  }

  private updateDisplay(): void {
    if (!this.currentData) return;
    const container = this.shadowRoot.querySelector('#cryptoExchangesContainer') as HTMLElement;
    if (isEmptyWidgetData(this.currentData.data)) {
      if (container) container.innerHTML = getNoDataHTML();
      return;
    }
    this.renderExchangeRows();
  }

  private trustFilter(row: any): boolean {
    const ts = String(row?.trust_score || "").toLowerCase();
    switch (this.selectedAction) {
      case "upgrade":
        return ts === "green";
      case "downgrade":
        return ts === "red";
      case "hold":
        return ts === "yellow";
      case "initialise":
        return true;
      default:
        return true;
    }
  }

  private renderExchangeRows(): void {
    const container = this.shadowRoot.querySelector(
      "#cryptoExchangesContainer"
    );
    if (!container) return;

    const rows = Array.isArray(this.currentData?.data)
      ? this.currentData.data
      : [];
    const filtered = rows.filter((item: any) => item && this.trustFilter(item));
// 👇 yahan lagao
  if (filtered.length > 0) {
    container.setAttribute("data-testing-id", "crypto-market");
  } else {
    container.removeAttribute("data-testing-id");
  }

    if (filtered.length === 0) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const fmtPrice = (n: unknown) => {
      const v = Number(n);
      if (!Number.isFinite(v)) return "—";
      return v.toLocaleString(undefined, {
        maximumFractionDigits: v >= 1000 ? 2 : 6,
      });
    };

    const trustClass = (ts: string) => {
      const t = ts.toLowerCase();
      if (t === "green") return "up";
      if (t === "red") return "down";
      return "mid";
    };

    const rowsHtml = filtered
      .map((item: any, index: number) => {
        const ts = String(item.trust_score ?? "");
        const tc = trustClass(ts);
        const exchangeName = String(item.exchange || "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "_");
        const logoUrl = `https://storage.googleapis.com/analytics-images-traderverse/crypto_exchanges/logos/exchange-logos/${exchangeName}.png`;
        return `
        <div class="item body">
          <p>${index + 1}</p>
          <p class="crypto-updated-col">${this.esc(item.last_updated_at)}</p>
          <p><img src="${logoUrl}" alt="${this.esc(item.exchange)}" width="20" height="20"> ${this.esc(item.exchange)}</p>
          <p>${this.esc(item.pair)}</p>
          <p class="cell-end">$${this.esc(fmtPrice(item.price))}</p>
          <p class="cell-end">${this.esc(item.spread)}</p>
          <p class="cell-end">${this.esc(item.volume_24hrs)}</p>
          <p class="cell-end">${this.esc(item.pos_two_percent_depth)}</p>
          <p class="cell-end">${this.esc(item.neg_two_percent_depth)}</p>
          <p class="stat ${tc}">
            <span>
              <i class="trust-dot"></i>
              ${this.esc(ts || "—")}
            </span>
          </p>
        </div>
      `;
      })
      .join("");

    const gridStyle =
      "--grid-template-columns: 0.38fr 0.95fr 1.35fr 1.05fr 1.05fr 0.62fr 0.88fr 1fr 1fr 0.62fr; " +
      "--grid-column-gap: 10px; " +
      "min-width: 960px;";

    container.innerHTML = `
      <div class="eventsMain style02 customGrid cryptoMarketExchanges" style="${gridStyle}">
        <div class="item header">
          <p>#</p>
          <p>Updated</p>
          <p>Exchange</p>
          <p>Pair</p>
          <p>Price</p>
          <p>Spread</p>
          <p>24h volume</p>
          <p>+2% depth</p>
          <p>-2% depth</p>
          <p>Trust Score</p>
        </div>
        <div class="bodyContainer" id="cryptoExchangesRows">${rowsHtml}</div>
      </div>
    `;
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    if (!stock) {
      const sym = String(stockId || "")
        .trim()
        .toUpperCase();
      const list = getSymbolOptions("crypto");
      const match =
        list.find((c) => c.stockId === sym) ||
        list.find((c) => c.symbol === sym) ||
        list.find((c) => sym.startsWith(`${c.symbol}_`));
      if (match) stock = { ...match };
    }
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      stockId,
      (s: any) => this.setupChart(s),
      (t: any, d: any) => this.emit(t, d)
    );
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list)
      this.eventHandlers.set(
        type,
        list.filter((h) => h !== handler)
      );
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector(
      ".symbol-dropdown"
    ) as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  public setTheme(theme: "white" | "black" | "default-dark" | "default-lite" | "default-lite"): void {
    if (!this.shadowRoot) return;

    this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as
      | "white"
      | "black"
      | "default-dark";

    this.updateDisplay();
  }
  public destroy(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = "";
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) handlers.forEach((h) => h({ type, data }));
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }
}

declare global {
  interface Window {
    TraderverseCryptoMarket: typeof TraderverseCryptoMarket;
  }
}
if (typeof window !== "undefined")
  window.TraderverseCryptoMarket = TraderverseCryptoMarket;
